import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

/**
 * 게스트 프리뷰 "전체 현황" 카드의 *내부 콘텐츠*만 렌더하는 공통 컴포넌트
 * (JCG — 게스트 목업 dedup).
 *
 * `GuestDashboardPreview`(/my 게스트 대시보드 프리뷰 3행 카드)와
 * `StudyProgressGuestMock`(/my/study-progress 게스트 탭)이 한 글자도 다르지 않게
 * 복붙하던 "전체 현황" 블록(진도 3행 + 정답률 + 시도/정답/오답 StatBox 3개)을
 * 단일 소스로 추출했다. **바깥 Card/제목 래퍼는 호출부가 각자 제공**한다(시각 동일 유지).
 *
 * - 훅/상태/데이터 fetch 없는 순수 presentational. 더미 데이터는 모듈 상수.
 * - 색은 디자인 토큰 + StatBox tone 맵의 light/dark 페어
 *   (ui-patterns dark-mode-colors §3-4 — 배경 `-50` ↔ `dark:-900/20`, 텍스트 `-600` ↔ `dark:-300`).
 * - ⚠️ 프로덕션 `dashboard/DashboardStudyProgress.tsx`(실제 로그인 위젯)와는 무관.
 *   이 컴포넌트는 게스트 목업 2곳 전용이다.
 */

/** 전체 현황 진도 행. */
const MOCK_PROGRESS_ROWS: { label: string; value: string; percent: number }[] = [
  { label: "이론 출석", value: "32 / 48 페이지", percent: (32 / 48) * 100 },
  { label: "코딩 출석", value: "18 / 25 페이지", percent: (18 / 25) * 100 },
  { label: "문제 시도", value: "210 / 320문제 (66%)", percent: 66 },
];

const STAT_TONE: Record<string, string> = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300",
  green: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300",
  red: "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300",
};

const MOCK_STATS: { label: string; value: number; tone: "blue" | "green" | "red" }[] = [
  { label: "시도", value: 210, tone: "blue" },
  { label: "정답", value: 132, tone: "green" },
  { label: "오답", value: 78, tone: "red" },
];

export default function GuestStudyProgressOverview() {
  return (
    <div className="space-y-4">
      {MOCK_PROGRESS_ROWS.map((row) => (
        <div key={row.label}>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{row.label}</span>
            <span className="text-sm font-medium text-foreground">
              {row.value}
            </span>
          </div>
          <Progress value={row.percent} className="h-2" />
        </div>
      ))}

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">시도 중 정답률</span>
        <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
          62.8%
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-1 text-center text-xs">
        {MOCK_STATS.map((stat) => (
          <div
            key={stat.label}
            className={cn("rounded-lg p-2", STAT_TONE[stat.tone])}
          >
            <p>{stat.label}</p>
            <p className="text-lg font-semibold">{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
